import clsx from 'clsx';
import { omit } from 'lodash';
import { object, string } from 'prop-types';
import { createContext, useContext } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Card, DatePicker, FormInput, RadioGroup } from 'components';
import { formatDate, getValidDate, processString } from 'helpers';
import { useCivility, useFetch, useSafeState, useUser } from 'hooks';

const UserContext = createContext();

const ActionButtons = ({ className = '' }) => {
  const { t } = useTranslation();
  const { isEditable, setIsEditable, setFieldData } = useContext(UserContext);

  return (
    <div className={clsx('flex justify-end items-center gap-8', className)}>
      {isEditable && (
        <>
          <button className="btn small" type="submit">
            {t('buttons.save')}
          </button>

          <button
            type="button"
            className="btn outlined small"
            onClick={() => {
              setIsEditable(false);
              setFieldData();
            }}
          >
            {t('buttons.cancel')}
          </button>
        </>
      )}

      {!isEditable && (
        <button type="button" className="btn small" onClick={() => setIsEditable(true)}>
          {t('buttons.modify')}
        </button>
      )}
    </div>
  );
};

const UserForm = ({ userInfos }) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { call: userInfosUpdateCall, loading: userInfosUpdateLoading } = useFetch();

  const [isEditable, setIsEditable] = useSafeState(false);
  const { civilityOptions } = useCivility('userCivility');

  const { register, handleSubmit, control, getValues, setValue, formState } = useForm({
    mode: 'all',
    defaultValues: {
      serialNumber: userInfos.serialNumber,
      firstname: userInfos.firstName,
      email: userInfos.email,
      name: userInfos.name,
      birthDate: getValidDate(userInfos.birthDate),
      mobilePhone: userInfos.mobilePhone,
      phone: userInfos.phone,
      userCivility: userInfos.civility === 'Mme' ? civilityOptions?.[1].value : civilityOptions?.[0].value
    }
  });

  const setFieldData = () => {
    setValue('serialNumber', userInfos.serialNumber);
    setValue('firstname', userInfos.firstName);
    setValue('email', userInfos.email);
    setValue('name', userInfos.name);
    setValue('birthDate', getValidDate(userInfos.birthDate));
    setValue('mobilePhone', userInfos.mobilePhone);
    setValue('phone', userInfos.phone);
    setValue('userCivility', userInfos.civility === 'Mme' ? civilityOptions?.[1].value : civilityOptions?.[0].value);
  };

  const onSubmit = async (newUserData) => {
    setIsEditable(false);
    if (!currentUser?.serialNumber || userInfosUpdateLoading) return;

    newUserData = {
      ...newUserData,
      birthDate: formatDate(newUserData.birthDate),
      civility: newUserData.userCivility,
      firstName: processString(newUserData.firstname),
      name: processString(newUserData.name)
    };
    newUserData = omit(newUserData, 'userCivility');

    // Remove user id because they can't change it
    newUserData = omit(newUserData, 'serialNumber');

    const { data } = await userInfosUpdateCall({
      service: 'castelis',
      path: `/users/${currentUser.serialNumber}`,
      method: 'put',
      query: newUserData
    });

    data?.status === 200 ? toast.success(t('dashboard:toast.success')) : toast.error(data?.body);
  };

  const providerValues = {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState
  };

  return (
    <Card className="lg:col-span-7" onMobile={false}>
      <UserContext.Provider value={{ isEditable, setIsEditable, setFieldData }}>
        <FormProvider {...providerValues}>
          <form onSubmit={handleSubmit(onSubmit)} className="user-form">
            <div className="flex justify-between w-full mb-6">
              <h2 className="h2 underlined-yellow">{t('dashboard:dataTab.userInfo.title')}</h2>
              <ActionButtons className="hidden lg:flex" />
            </div>

            <RadioGroup
              radioOptions={civilityOptions}
              groupLabel={t('form:civility.label')}
              row
              className="grid grid-cols-12 mb-5"
              disabled={!isEditable}
            />

            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:fullname')}</p>
              <FormInput
                name="name"
                disabled={!isEditable}
                className="col-span-4"
                placeholder={t('form:lastname')}
                onBlur={(value) => setValue('name', processString(value))}
              />
              <FormInput
                name="firstname"
                disabled={!isEditable}
                className="col-span-4"
                placeholder={t('form:firstname')}
                onBlur={(value) => setValue('firstname', processString(value))}
              />
            </div>

            <Controller
              name="birthDate"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  label={t('form:birthdate')}
                  selected={value}
                  onChange={onChange}
                  containerClass="grid grid-cols-12 gap-4 items-center mb-5"
                  disabled={!isEditable}
                  name="bithdate"
                  placeholder="jj/mm/aaaa"
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={40}
                  openToDate={getValidDate(userInfos.birthDate)}
                />
              )}
            />

            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:email')}</p>
              <FormInput name="email" type="email" disabled={!isEditable} className="col-span-8" />
            </div>

            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:mobilePhone')}</p>
              <FormInput type="tel" name="mobilePhone" disabled={!isEditable} className="col-span-8" />
            </div>

            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:phone')}</p>
              <FormInput type="tel" name="phone" disabled={!isEditable} className="col-span-8" />
            </div>

            <div className="grid grid-cols-12 w-full gap-4 items-center mb-5">
              <p className={clsx('label col-span-4', !isEditable && 'disabled')}>{t('form:serialNumber')}</p>
              <FormInput name="serialNumber" disabled={true} className="col-span-8" />
            </div>

            <ActionButtons className="lg:hidden" />
          </form>
        </FormProvider>
      </UserContext.Provider>
    </Card>
  );
};

UserForm.propTypes = {
  userInfos: object
};

ActionButtons.propTypes = {
  className: string
};

export default UserForm;
